import type { Observable } from 'rxjs';
import { catchError, map } from 'rxjs';

import { assertValue } from '@libs/utils';
import type {
  FerryTicketCategory,
  FerryTicketCategoryResponse,
  PortStaticInfo,
  SeaportStaticInfo,
  TransportVehicle,
  URLString,
} from '@libs/trip-planner-interfaces';
import { getMarketOrigin } from '@libs/search-service-smart-search';

import { getErrorMessage } from '../plugins/errorMessage';

import { ContentApi } from './content/contentApi';
import type {
  ContentApiFerryRouteStaticData,
  ContentApiFerrySuggestionRequestData,
} from './content/contentApiResponses';
import { ApiQuery } from './shared/apiQuery';

export class FerryEndpoint {
  private contentApi = new ContentApi();
  private smartSearchApi: ApiQuery;

  private readonly smartSearchUrl = 'https://smart-search.sembo.com/';
  private readonly ferryTicketCategoriesUrl =
    'ferry-ticket-categories/get-categories';

  constructor() {
    this.smartSearchApi = new ApiQuery(this.smartSearchUrl);
  }

  public getSeaportStaticDataByCode(code: string): Observable<PortStaticInfo> {
    return this.contentApi.searchSeaportStaticInfo(code, 1).pipe(
      map((seaports: SeaportStaticInfo[]) => {
        const port = seaports[0];
        assertValue(port, 'ferry.ts > getSeaportStaticDataByCode > port');

        return {
          code: port.code,
          name: port.label,
          position: {
            name: port.label,
            latitude: port.latitude,
            longitude: port.longitude,
          },
        };
      })
    );
  }

  public searchFerryRoutes(
    searchTerm: string,
    isStenaLineAffiliateInURL: boolean
  ): Observable<ContentApiFerryRouteStaticData[]> {
    const maxFerryRoutes = 40;
    return this.contentApi.searchFerryRoutes(
      searchTerm,
      maxFerryRoutes,
      isStenaLineAffiliateInURL
    );
  }

  public getFerryVehicleTypes(): Observable<TransportVehicle[]> {
    return this.contentApi.getFerryVehicleTypes();
  }

  public getFerryRoutesSuggestions(
    from: ContentApiFerrySuggestionRequestData,
    to: ContentApiFerrySuggestionRequestData,
    isStenaLine: boolean
  ): Observable<ContentApiFerryRouteStaticData[]> {
    return this.contentApi.getFerryRoutesSuggestions(from, to, isStenaLine);
  }

  public getFerryRouteStaticInfo(
    harbourFrom: string,
    harbourTo: string
  ): Observable<ContentApiFerryRouteStaticData | null> {
    return this.contentApi.searchFerryRouteStaticInfo(harbourFrom, harbourTo);
  }

  public getFerryTicketCategories(
    key: string
  ): Observable<FerryTicketCategory[]> {
    return this.smartSearchApi
      .postObservable<FerryTicketCategoryPayload, FerryTicketCategoryResponse>(
        this.ferryTicketCategoriesUrl,
        {
          token: `Type=flight&Identifier=${key}`,
          market: getMarketOrigin(),
        }
      )
      .pipe(
        map((categories) => categories.options),
        catchError((error) => {
          const errorMessage = getErrorMessage(error);
          throw new Error(
            `Failed to fetch ferry ticket categories. Error: ${errorMessage}`
          );
        })
      );
  }
}

interface FerryTicketCategoryPayload {
  token: string;
  market: URLString;
}
